import type {
  MetaMaskSDK,
  MetaMaskSDKOptions,
  RPC_URLS_MAP,
  SDKProvider,
} from '@metamask/sdk';
import type { ConnectorData } from '@wagmi/core';
import { Connector } from '@wagmi/core';
import type {
  Address,
  Chain,
  Hex,
  ProviderConnectInfo,
  ProviderRpcError,
  RpcError,
} from 'viem';
import {
  createWalletClient,
  custom,
  getAddress,
  hexToNumber,
  numberToHex,
  ResourceUnavailableRpcError,
  SwitchChainError,
  UserRejectedRequestError,
} from 'viem';

import { getCurrentChainId } from '../../../smart-contract-utils';
import { detector } from './detector';
import type { Compute, ExactPartial, OneOf, UnionCompute } from './types';
import { extractRpcUrls, withRetry, withTimeout } from './utils';

export type MetaMaskParameters = UnionCompute<
  WagmiMetaMaskSDKOptions &
    OneOf<
      | { connectAndSign?: string }
      | { connectWith?: { method: string; params: unknown[] } }
    >
>;

type WagmiMetaMaskSDKOptions = Compute<
  ExactPartial<
    Omit<
      MetaMaskSDKOptions,
      | '_source'
      | 'forceDeleteProvider'
      | 'forceInjectProvider'
      | 'injectProvider'
      | 'useDeeplink'
      | 'readonlyRPCMap'
    >
  > & {
    /** @deprecated */
    forceDeleteProvider?: MetaMaskSDKOptions['forceDeleteProvider'];
    /** @deprecated */
    forceInjectProvider?: MetaMaskSDKOptions['forceInjectProvider'];
    /** @deprecated */
    injectProvider?: MetaMaskSDKOptions['injectProvider'];
    /** @deprecated */
    useDeeplink?: MetaMaskSDKOptions['useDeeplink'];
  }
>;

type Listener = Parameters<SDKProvider['on']>[1];

interface StorageStoreData {
  state: { data?: ConnectorData };
}

const getSDK = async (): Promise<typeof MetaMaskSDK> => {
  const { default: SDK } = await import('@metamask/sdk');

  /** @ts-expect-error */
  if (typeof SDK !== 'function' && typeof SDK.default === 'function') {
    /** @ts-expect-error */
    return SDK.default;
  }

  /** @ts-expect-error */
  return SDK as unknown as typeof SDK.default;
};

const STORE_KEY = 'store';

export class MetaMaskConnector extends Connector {
  id = 'metaMaskSDK';
  name = 'MetaMask';
  ready = true;

  params: MetaMaskParameters;

  provider: SDKProvider | null = null;
  providerPromise: Promise<typeof this.provider> | null = null;
  sdk: MetaMaskSDK | null = null;

  _listeners = {
    displayUri: null as null | typeof this.onDisplayUri,
    connect: null as null | typeof this.onConnect,
    disconnect: null as null | Listener,
    chainChanged: null as null | typeof this.onChainChanged,
    accountsChanged: null as null | typeof this.onAccountsChanged,
  };

  constructor({
    params = {},
    chains,
  }: {
    chains?: Chain[];
    params?: MetaMaskParameters;
  }) {
    super({ chains, options: params });

    this.params = params;

    void this.setup();
  }

  async setup() {
    const provider = await this.getProvider();
    if (provider?.on) {
      if (!this._listeners.connect) {
        this._listeners.connect = this.onConnect.bind(this);
        provider.on('connect', this._listeners.connect as Listener);
      }

      /**
       * We shouldn't need to listen for `'accountsChanged'` here since the `'connect'`
       * event should suffice (and wallet shouldn't be connected yet)
       * Some wallets, like MetaMask, do not implement the `'connect'` event and
       * overload `'accountsChanged'` instead.
       */
      if (!this._listeners.accountsChanged) {
        this._listeners.accountsChanged = this.onAccountsChanged.bind(this);
        provider.on(
          'accountsChanged',
          this._listeners.accountsChanged as Listener,
        );
      }

      if (!this._listeners.chainChanged) {
        this._listeners.chainChanged = this.onChainChanged.bind(this);
        provider.on('chainChanged', this._listeners.chainChanged as Listener);
      }

      this.ready = detector();
    }
  }

  async isAuthorized() {
    try {
      const timeout = 200;
      const accounts = await withRetry(
        () => withTimeout(() => this.getAccounts(), { timeout }),
        { delay: timeout + 1, retryCount: 3 },
      );

      return Boolean(accounts.length);
    } catch {
      return false;
    }
  }

  async connect({ chainId }: { chainId?: number } = {}) {
    const provider = await this.getProvider();

    if (!this._listeners.displayUri) {
      this._listeners.displayUri = this.onDisplayUri.bind(this);
      provider.on('display_uri', this._listeners.displayUri);
    }

    let accounts: readonly Address[] = [];

    try {
      let signResponse: unknown;
      let connectWithResponse: unknown;

      if (!accounts?.length) {
        if (this.params.connectAndSign || this.params.connectWith) {
          if (this.params.connectAndSign) {
            signResponse = await this.sdk!.connectAndSign({
              msg: this.params.connectAndSign,
            });
          } else if (this.params.connectWith) {
            connectWithResponse = await this.sdk!.connectWith({
              method: this.params.connectWith.method,
              params: this.params.connectWith.params,
            });
          }

          accounts = await this.getAccounts();
        } else {
          const requestedAccounts = await this.sdk!.connect();
          accounts = requestedAccounts.map(x => getAddress(x));
        }
      }

      let currentChainId = await this.getChainId();
      let targetChainId = chainId ?? getCurrentChainId();

      if (!targetChainId) {
        const store = this.storage?.getItem<StorageStoreData>(STORE_KEY);
        const lastUsedChainId = store?.state?.data?.chain?.id;

        if (lastUsedChainId && !this.isChainUnsupported(lastUsedChainId)) {
          targetChainId = lastUsedChainId;
        } else {
          targetChainId = this.chains[0]?.id;
        }
      }

      if (targetChainId && currentChainId !== targetChainId) {
        const chain = await this.switchChain(targetChainId).catch(error => {
          if (error.code === UserRejectedRequestError.code) {
            throw error;
          }

          const unsupported = this.isChainUnsupported(currentChainId);

          return { id: currentChainId, unsupported };
        });

        currentChainId = chain?.id ?? currentChainId;
      }

      if (this._listeners.displayUri) {
        provider.removeListener('display_uri', this._listeners.displayUri);
        this._listeners.displayUri = null;
      }

      if (signResponse) {
        provider.emit('connectAndSign', {
          accounts,
          chainId: currentChainId,
          signResponse,
        });
      } else if (connectWithResponse) {
        provider.emit('connectWith', {
          accounts,
          chainId: currentChainId,
          connectWithResponse,
        });
      }

      if (this._listeners.connect) {
        provider.removeListener('connect', this._listeners.connect);
        this._listeners.connect = null;
      }

      if (!this._listeners.accountsChanged) {
        this._listeners.accountsChanged = this.onAccountsChanged.bind(this);
        provider.on(
          'accountsChanged',
          this._listeners.accountsChanged as Listener,
        );
      }

      if (!this._listeners.chainChanged) {
        this._listeners.chainChanged = this.onChainChanged.bind(this);
        provider.on('chainChanged', this._listeners.chainChanged as Listener);
      }

      if (!this._listeners.disconnect) {
        this._listeners.disconnect = this.onDisconnect.bind(this);
        provider.on('disconnect', this._listeners.disconnect);
      }

      const unsupported = this.isChainUnsupported(currentChainId);

      return {
        account: accounts[0],
        chain: { id: currentChainId, unsupported },
      };
    } catch (err) {
      const error = err as RpcError;

      if (error.code === UserRejectedRequestError.code) {
        throw new UserRejectedRequestError(error);
      }

      if (error.code === ResourceUnavailableRpcError.code) {
        throw new ResourceUnavailableRpcError(error);
      }

      throw error;
    }
  }

  async disconnect() {
    const provider = await this.getProvider();

    if (this._listeners.chainChanged) {
      provider.removeListener('chainChanged', this._listeners.chainChanged);
      this._listeners.chainChanged = null;
    }

    if (this._listeners.disconnect) {
      provider.removeListener('disconnect', this._listeners.disconnect);
      this._listeners.disconnect = null;
    }

    if (!this._listeners.connect) {
      this._listeners.connect = this.onConnect.bind(this);
      provider.on('connect', this._listeners.connect as Listener);
    }

    await this.sdk?.terminate();
  }

  async switchChain(chainId: number) {
    const provider = await this.getProvider();
    const chain = this.chains.find(x => x.id === chainId);

    if (!chain) {
      throw new SwitchChainError(new Error('Chain not configured'));
    }

    try {
      await Promise.all([
        provider
          .request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: numberToHex(chainId) }],
          })
          .then(async () => {
            const currentChainId = await this.getChainId();

            if (currentChainId !== chainId) {
              return;
            }

            const unsupported = this.isChainUnsupported(chainId);

            this.emit('change', { chain: { id: chainId, unsupported } });
          }),
        new Promise<void>(resolve => {
          const listener = ({ chain: _chain }: ConnectorData) => {
            if (_chain?.id !== chainId) {
              return;
            }

            this.off('change', listener);
            resolve();
          };

          this.on('change', listener);
        }),
      ]);

      return chain;
    } catch (switchChainError) {
      const error = switchChainError as RpcError;

      if (
        error.code === 4902 ||
        (error as ProviderRpcError<{ originalError?: { code: number } }>)?.data
          ?.originalError?.code === 4902
      ) {
        try {
          await provider.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: numberToHex(chain.id),
                blockExplorerUrls: [chain.blockExplorers?.default?.url],
                chainName: chain.name,
                nativeCurrency: chain.nativeCurrency,
                rpcUrls: [...chain.rpcUrls.default.http],
              },
            ],
          });

          const currentChainId = hexToNumber(
            (await provider.request({ method: 'eth_chainId' })) as Hex,
          );

          if (currentChainId !== chainId) {
            throw new UserRejectedRequestError(
              new Error('User rejected switch after adding network.'),
            );
          }

          return chain;
        } catch (addChainError) {
          throw new UserRejectedRequestError(addChainError as Error);
        }
      }

      if (error.code === UserRejectedRequestError.code) {
        throw new UserRejectedRequestError(error);
      }

      throw new SwitchChainError(error);
    }
  }

  async getChainId() {
    const provider = await this.getProvider();
    const chainId =
      provider.getChainId() ||
      (await provider?.request({ method: 'eth_chainId' }));

    return Number(chainId);
  }

  async getAccounts() {
    const provider = await this.getProvider();
    const accounts = (await provider.request({
      method: 'eth_accounts',
    })) as string[];

    return accounts.map(x => getAddress(x));
  }

  async getAccount() {
    const accounts = await this.getAccounts();

    return accounts[0];
  }

  async getWalletClient({ chainId }: { chainId?: number } = {}) {
    const [provider, account] = await Promise.all([
      this.getProvider(),
      this.getAccount(),
    ]);

    if (!provider) {
      throw new Error('provider is required.');
    }

    const chain = this.chains.find(x => x.id === chainId)!;

    return createWalletClient({
      account,
      chain,
      transport: custom(provider),
    });
  }

  async getProvider({ chainId }: { chainId?: number } = {}) {
    if (!this.provider) {
      if (!this.providerPromise) {
        this.providerPromise = this.initProvider();
      }

      this.provider = await this.providerPromise;
    }

    if (chainId) {
      await this.switchChain(chainId);
    }

    return this.provider!;
  }

  async initProvider() {
    const SDK = await getSDK();

    const readonlyRPCMap = this.chains.reduce<RPC_URLS_MAP>((acc, chain) => {
      acc[numberToHex(chain.id)] = extractRpcUrls({ chain })?.[0];

      return acc;
    }, {});

    const dappMetadata =
      this.params.dappMetadata ??
      (typeof window !== 'undefined'
        ? { url: window.location.origin }
        : { name: 'wagmi', url: 'https://wagmi.sh' });

    this.sdk = new SDK({
      _source: 'wagmi',
      forceDeleteProvider: false,
      forceInjectProvider: false,
      injectProvider: false,
      ...this.params,
      readonlyRPCMap,
      dappMetadata,
      useDeeplink: this.params.useDeeplink ?? true,
    });

    await this.sdk.init();

    return this.sdk.getProvider()!;
  }

  async onConnect(connectInfo: ProviderConnectInfo) {
    const account = await this.getAccount();

    if (account) {
      return;
    }

    const id = Number(connectInfo.chainId);
    const unsupported = this.isChainUnsupported(id);

    this.emit('connect', { account, chain: { id, unsupported } });

    const provider = await this.getProvider();

    if (this._listeners.connect) {
      provider.removeListener('connect', this._listeners.connect);
      this._listeners.connect = null;
    }

    if (!this._listeners.accountsChanged) {
      this._listeners.accountsChanged = this.onAccountsChanged.bind(this);
      provider.on(
        'accountsChanged',
        this._listeners.accountsChanged as Listener,
      );
    }

    if (!this._listeners.chainChanged) {
      this._listeners.chainChanged = this.onChainChanged.bind(this);
      provider.on('chainChanged', this._listeners.chainChanged as Listener);
    }

    if (!this._listeners.disconnect) {
      this._listeners.disconnect = this.onDisconnect.bind(this);
      provider.on('disconnect', this._listeners.disconnect);
    }
  }

  async onDisconnect(error?: any) {
    const provider = await this.getProvider();

    if (
      error &&
      (error as RpcError<1013>).code === 1013 &&
      this.provider &&
      !!(await this.getAccounts()).length
    ) {
      return;
    }

    this.emit('disconnect');

    // Manage EIP-1193 event listeners
    if (this._listeners.chainChanged) {
      provider.removeListener('chainChanged', this._listeners.chainChanged);
      this._listeners.chainChanged = null;
    }

    if (this._listeners.disconnect) {
      provider.removeListener('disconnect', this._listeners.disconnect);
      this._listeners.disconnect = null;
    }

    if (!this._listeners.connect) {
      this._listeners.connect = this.onConnect.bind(this);
      provider.on('connect', this._listeners.connect as Listener);
    }
  }

  async onAccountsChanged(accounts: Address[]) {
    // Disconnect if there are no accounts
    if (accounts.length === 0) {
      // ... and using browser extension
      if (this.sdk?.isExtensionActive()) {
        void this.onDisconnect();
      }
    } else if (this.listenerCount('connect')) {
      // Connect if emitter is listening for connect event (e.g. is disconnected and connects through wallet interface)
      const chainId = (await this.getChainId()).toString();

      void this.onConnect({ chainId });
    } else {
      // Regular change event
      const account = accounts[0];

      this.emit('change', { account });
    }
  }

  onChainChanged(chainId: number | string) {
    const id = Number(chainId);
    const unsupported = this.isChainUnsupported(id);

    this.emit('change', { chain: { id, unsupported } });
  }

  onDisplayUri(uri: any) {
    this.emit('message', { type: 'display_uri', data: uri });
  }
}
